// Landing Page View
import { t, getLanguage, setLanguage } from '../i18n.js';
import '../styles/landing.css';

/**
 * Create landing page view
 * @param {Object} currentUser - optional user state
 * @returns {string} HTML string
 */
export function createLandingView(currentUser) {
  const currentLang = getLanguage();
  const nextLang = currentLang === 'tr' ? 'en' : 'tr';
  const langToggleText = currentLang === 'tr' ? 'EN' : 'TR';

  return `
    <div class="landing-page">
      <nav class="lp-nav">
        <div class="lp-nav-inner">
          <a class="lp-nav-logo" href="#/">
            <span class="icon">🏛️</span>
            <span>ArchaeoHub</span>
          </a>
          <div class="lp-nav-links">
            <a href="javascript:void(0)" onclick="document.getElementById('pricing').scrollIntoView({behavior: 'smooth'})">${t('credits.creditLabel')}</a>
          </div>
          <div class="lp-nav-cta">
            <button class="lp-btn lp-btn-ghost lp-lang-toggle" data-next-lang="${nextLang}">
              🌐 ${langToggleText}
            </button>
            <a class="lp-btn lp-btn-primary" href="${currentUser ? '#/dashboard' : '#/login'}">${currentUser ? t('nav.dashboard') : t('login.button')}</a>
          </div>
        </div>
      </nav>

      <section class="lp-hero">
        <div class="lp-hero-glow"></div>
        <div class="lp-tag fade-up">${t('landing.heroTag')}</div>
        <h1 class="fade-up">
          ${t('landing.heroTitle').split(' ').slice(0, -2).join(' ')}
          <span class="accent">${t('landing.heroTitle').split(' ').slice(-2).join(' ')}</span>
        </h1>
        <p class="lp-hero-sub fade-up">${t('landing.heroSub')}</p>
        <div class="lp-hero-actions fade-up">
          <a class="lp-btn lp-btn-primary" href="${currentUser ? '#/dashboard' : '#/login'}">${currentUser ? t('nav.dashboard') : t('landing.startFree')}</a>
          <a class="lp-btn lp-btn-ghost" href="javascript:void(0)" onclick="document.getElementById('features').scrollIntoView({behavior: 'smooth'})">${t('landing.explore')}</a>
        </div>

        <div class="lp-hero-stats fade-up">
          <div class="lp-stat-item">
            <div class="lp-stat-number">3D</div>
            <div class="lp-stat-label">${t('landing.stats3D')}</div>
          </div>
          <div class="lp-stat-item">
            <div class="lp-stat-number">AI</div>
            <div class="lp-stat-label">${t('landing.statsAI')}</div>
          </div>
          <div class="lp-stat-item">
            <div class="lp-stat-number">2</div>
            <div class="lp-stat-label">${t('landing.statsLang')}</div>
          </div>
          <div class="lp-stat-item">
            <div class="lp-stat-number">∞</div>
            <div class="lp-stat-label">${t('landing.statsSites')}</div>
          </div>
        </div>
      </section>

      <section class="lp-section" id="features">
        <div class="container">
          <div class="lp-section-header">
            <div class="lp-tag">✨ Features</div>
            <h2>${t('landing.featuresTitle')}</h2>
            <p>${t('landing.featuresSub')}</p>
          </div>
          <div class="lp-features-grid">
            <div class="lp-feature-card">
              <span class="lp-feature-icon">📊</span>
              <h3>${t('site.tabDetails')} ${t('landing.feature1Title')}</h3>
              <p>${t('landing.feature1Desc')}</p>
            </div>
            <div class="lp-feature-card">
              <span class="lp-feature-icon">🗿</span>
              <h3>${t('site.tabAssets')}</h3>
              <p>${t('landing.feature2Desc')}</p>
            </div>
            <div class="lp-feature-card">
              <span class="lp-feature-icon">🧩</span>
              <h3>${t('landing.statsAI')}</h3>
              <p>${t('landing.feature3Desc')}</p>
            </div>
            <div class="lp-feature-card">
              <span class="lp-feature-icon">📄</span>
              <h3>${t('landing.feature4Title')}</h3>
              <p>${t('landing.feature4Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="lp-section" id="pricing" style="background: var(--lp-bg2);">
        <div class="container">
          <div class="lp-section-header">
            <div class="lp-tag">💳 ${t('credits.creditLabel')}</div>
            <h2>${t('landing.pricingTitle')}</h2>
            <p>${t('landing.pricingSub')}</p>
          </div>
          <div class="lp-pricing-grid">
            ${[1, 3, 5, 10, 15, 20, 50].map(amount => `
              <div class="lp-price-card ${amount === 5 ? 'featured' : ''}">
                <span class="lp-price-amount">$${amount}</span>
                <div class="lp-price-credits">${amount} ${t('credits.creditLabel')}</div>
              </div>
            `).join('')}
          </div>
          <p class="lp-pricing-note" style="text-align:center; margin-top:2rem; margin-bottom: 0.5rem; color:var(--lp-muted);">
            ${t('landing.pricingUserNote')}
          </p>
          <p class="lp-pricing-note" style="text-align:center; color:var(--lp-muted);">
            ${t('landing.pricingNote')}
          </p>
        </div>
      </section>

      <section class="lp-section lp-cta-banner" style="text-align:center;">
        <div class="container">
          <h2>${t('landing.ctaTitle')}</h2>
          <p>${t('landing.ctaSub')}</p>
          <a class="lp-btn lp-btn-primary" href="${currentUser ? '#/dashboard' : '#/login'}" style="font-size:1.2rem; padding:1rem 3rem;">
            ${currentUser ? t('nav.dashboard') : t('landing.startFree')}
          </a>
        </div>
      </section>

      <footer class="lp-footer">
        <div class="container">
          <div class="lp-footer-inner">
            <div class="lp-nav-logo">
              <span>🏛️</span>
              <span>ArchaeoHub</span>
            </div>
            <div class="lp-footer-links">
              <a href="privacy-policy.html">${t('landing.privacy')}</a>
              <a href="mailto:${t('landing.contactEmail')}">${t('landing.contactEmail')}</a>
            </div>
            <div class="lp-footer-copy">${t('landing.footerCopy')}</div>
          </div>
        </div>
      </footer>
    </div>
  `;
}

/**
 * Initialize landing page events
 */
export function initLandingView() {
  const langBtn = document.querySelector('.lp-lang-toggle');
  if (langBtn) {
    langBtn.addEventListener('click', (e) => {
      const nextLang = e.currentTarget.dataset.nextLang;
      setLanguage(nextLang);
    });
  }
}
